import { useEffect } from 'react'

import { useLocation, useNavigate } from 'react-router-dom'

import { logger } from 'src/utils/logger'

/**
 * OAuth Callback Detector Component
 *
 * This component monitors the URL for OAuth/OIDC callback parameters (code and state)
 * and automatically redirects to the SSO callback route when detected.
 *
 * **How it works:**
 * 1. When an Identity Provider (IdP) redirects back to the app after authentication,
 *    it appends `?code=...&state=...` query parameters to the URL
 * 2. This component detects those parameters on any route
 * 3. It redirects to the `/sso-callback` route while preserving the parameters
 * 4. The SSOCallback component then processes the OAuth callback
 *
 * **Why this is needed:**
 * The IdP can redirect users back to any URL in the app (wherever they initiated SSO login from).
 * This component ensures OAuth callbacks are always processed by the dedicated SSOCallback route,
 * regardless of which route the user was originally on.
 *
 * **Implementation notes:**
 * - Uses a wrapper pattern to monitor all routes via React Router context
 * - Performs a replace navigation to avoid adding to browser history
 * - Cleans up query parameters after redirect for cleaner URL display
 *
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render (typically Routes)
 * @returns {React.ReactNode} The children components unchanged (wrapper component)
 *
 * @example
 * <HashRouter>
 *   <OAuthCallbackDetector>
 *     <Routes>
 *       <Route path="/login" element={<Login />} />
 *       <Route path="/sso-callback" element={<SSOCallback />} />
 *     </Routes>
 *   </OAuthCallbackDetector>
 * </HashRouter>
 */
export default function OAuthCallbackDetector({ children }) {
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    // Check if we have OAuth callback query parameters (code and state)
    // These appear when IdP redirects back to our app
    const urlParams = new URLSearchParams(window.location.search)
    const code = urlParams.get('code')
    const state = urlParams.get('state')

    // If we have both code and state, and we're not already on the callback route
    if (code && state && location.pathname !== '/sso-callback') {
      logger.log('[OAuth Callback] Detected OAuth callback, redirecting to /sso-callback')

      // Preserve the query parameters and navigate to callback route
      navigate(`/sso-callback${window.location.search}`, { replace: true })

      // Clean up the URL by removing query params from the browser's address bar
      // This is cosmetic - the SSOCallback component will read them before they're removed
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname + window.location.hash,
        )
      }, 100)
    }
  }, [navigate, location])

  return children
}
